import { type ComponentType, lazy, type LazyExoticComponent } from "react";

const RELOAD_KEY = "naas:chunk-reload-at";

/**
 * `React.lazy` with automatic recovery from a stale-chunk failure.
 *
 * Vite content-hashes each route chunk, so after a deploy the hashes change and
 * the old chunks are gone. A browser that had the app open across the deploy
 * still references the OLD hashes; navigating to a not-yet-loaded route then
 * 404s on the missing chunk (the server returns an HTML error body, so the
 * module loader reports "disallowed MIME type (text/html)") and the page goes
 * blank.
 *
 * On the first such failure we force a full reload, which fetches the fresh
 * index.html + the new chunk graph and recovers transparently. Guarded by a
 * timestamp so a genuinely-broken chunk can't reload-loop: if the same import
 * fails again within 10s we let the error surface instead of reloading.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own signature
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() =>
    factory().catch((err: unknown) => {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
        window.location.reload();
        // The reload navigates away before React renders anything; never resolve.
        return new Promise<{ default: T }>(() => {});
      }
      throw err; // reloaded recently and still failing — surface the real error
    }),
  );
}
